import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LayoutDashboard, LogOut, Menu, X } from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { logout } from '@/services/authService';
import type { TranslationKey } from '@/constants/i18n';

const NAV_LINKS: { href: string; key: TranslationKey; isRoute?: boolean }[] = [
  { href: '/#features', key: 'nav.features' },
  { href: '/#platforms', key: 'nav.platforms' },
  { href: '/#faq', key: 'nav.faq' },
  { href: ROUTES.about, key: 'nav.about', isRoute: true },
];

export function Navbar() {
  const { status, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignOut = async () => {
    await logout();
    navigate(ROUTES.home);
  };

  const renderLink = (link: (typeof NAV_LINKS)[number]) =>
    link.isRoute ? (
      <Link
        key={link.href}
        to={link.href}
        onClick={() => setMobileOpen(false)}
        className="text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-100"
      >
        {t(link.key)}
      </Link>
    ) : (
      <a
        key={link.href}
        href={link.href}
        onClick={() => setMobileOpen(false)}
        className="text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-100"
      >
        {t(link.key)}
      </a>
    );

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-white/5 bg-surface/70 backdrop-blur-xl">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />

        <div className="hidden items-center gap-8 md:flex">{NAV_LINKS.map(renderLink)}</div>

        <div className="hidden items-center gap-3 md:flex">
          {status === 'signedIn' ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => navigate(ROUTES.dashboard)}>
                <LayoutDashboard className="h-4 w-4" aria-hidden />
                {t('nav.dashboard')}
              </Button>
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName ?? 'Account'}
                  referrerPolicy="no-referrer"
                  className="h-9 w-9 rounded-full border border-white/10"
                />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500/20 text-sm font-bold text-primary-300">
                  {(user?.displayName ?? user?.email ?? 'U').charAt(0).toUpperCase()}
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={handleSignOut} aria-label={t('nav.signOut')}>
                <LogOut className="h-4 w-4" aria-hidden />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.login)}>
                {t('nav.signIn')}
              </Button>
              <Button size="sm" onClick={() => navigate(ROUTES.register)}>
                {t('nav.getStarted')}
              </Button>
            </>
          )}
        </div>

        <button
          className="rounded-xl p-2 text-zinc-300 hover:bg-white/5 focus-ring md:hidden"
          onClick={() => setMobileOpen((open) => !open)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/5 bg-surface/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-4 px-6 py-5">
              {NAV_LINKS.map(renderLink)}
              <div className="flex flex-col gap-2 border-t border-white/5 pt-4">
                {status === 'signedIn' ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setMobileOpen(false);
                        navigate(ROUTES.dashboard);
                      }}
                    >
                      <LayoutDashboard className="h-4 w-4" aria-hidden />
                      {t('nav.dashboard')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleSignOut}>
                      <LogOut className="h-4 w-4" aria-hidden />
                      {t('nav.signOut')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setMobileOpen(false);
                        navigate(ROUTES.login);
                      }}
                    >
                      {t('nav.signIn')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setMobileOpen(false);
                        navigate(ROUTES.register);
                      }}
                    >
                      {t('nav.getStarted')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
